<template>
	<div class="stock-check-requests w-full">
		<!-- Header -->
		<div class="pt-0">
			<app-title-bar :icon="['fas', 'fa-list']" subtitle="Stock Management" title="Stock Check Requests">
				<div class="flex items-center space-x-3">
					<app-badge v-if="pendingItemCount > 0" theme="warning" size="medium">
						{{ pendingItemCount }} Pending Items
						<span v-if="uniqueDepotCount" class="ml-1 text-xs opacity-75">
							across {{ uniqueDepotCount }} depots
						</span>
					</app-badge>
					<app-button :loading="loading" size="small" theme="outline" @click="refreshData">
						<font-awesome-icon icon="fas fa-sync" class="mr-2" />
						Refresh
					</app-button>
				</div>
			</app-title-bar>
		</div>

		<!-- Filters -->
		<div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
			<div class="flex gap-3 items-center flex-wrap">
				<div class="flex-1 min-w-[180px] max-w-md">
					<div class="relative">
						<input
							v-model="searchFilter"
							type="text"
							placeholder="Search by item code, description, contract..."
							class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400" />
						<font-awesome-icon icon="fas fa-search" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
					</div>
				</div>
				<div class="flex gap-2">
					<button
						v-for="option in statusFilterOptions"
						:key="option.value"
						:class="[
							'px-3 py-1.5 text-xs rounded-md border transition-all',
							statusFilter === option.value
								? 'bg-mabey-600 text-white border-mabey-600'
								: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
						]"
						@click="statusFilter = option.value">
						{{ option.label }}
					</button>
				</div>
			</div>
		</div>

		<!-- Loading State -->
		<div v-if="loading && depotGroups.length === 0" class="flex justify-center items-center py-12">
			<app-loader />
		</div>

		<!-- Empty State -->
		<div v-else-if="!loading && filteredDepotGroups.length === 0" class="bg-white rounded-lg border border-gray-200 p-12 text-center">
			<font-awesome-icon icon="fas fa-clipboard-check" class="text-gray-300 text-5xl mb-4" />
			<h3 class="text-lg font-medium text-gray-900 mb-2">No stock check requests</h3>
			<p class="text-gray-600">
				{{ depotGroups.length === 0 ? 'No items are pending stock check.' : 'No items match your current filters.' }}
			</p>
		</div>

		<!-- Depot Groups -->
		<div v-else class="space-y-4">
			<div v-for="depot in filteredDepotGroups" :key="depot.depotName" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
				<!-- Depot Header -->
				<div
					class="bg-gray-50 px-5 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
					@click="toggleDepot(depot.depotName)">
					<div class="flex items-center justify-between">
						<div class="flex items-center space-x-3">
							<svg class="w-5 h-5 text-gray-400 transition-transform" :class="{'rotate-90': expandedDepots.has(depot.depotName)}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
							</svg>
							<font-awesome-icon icon="fas fa-warehouse" class="text-mabey-600 text-lg" />
							<div>
								<h3 class="text-base font-semibold text-gray-900">{{ depot.depotName }}</h3>
								<div class="flex items-center gap-3 mt-0.5">
									<span class="text-xs text-gray-500">{{ depot.items.length }} item{{ depot.items.length !== 1 ? 's' : '' }}</span>
									<span v-if="depot.pendingCount > 0" class="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
										{{ depot.pendingCount }} pending
									</span>
									<span v-if="depot.checkedCount > 0" class="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
										{{ depot.checkedCount }} checked
									</span>
								</div>
							</div>
						</div>
						<div class="flex items-center space-x-3" @click.stop>
							<!-- Progress bar -->
							<div class="w-32">
								<div class="flex items-center justify-between text-xs mb-1">
									<span class="text-gray-500">Progress</span>
									<span class="font-semibold" :class="depot.checkedCount === depot.items.length ? 'text-green-600' : 'text-gray-700'">
										{{ depot.checkedCount }}/{{ depot.items.length }}
									</span>
								</div>
								<div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
									<div
										class="h-full transition-all duration-300 rounded-full"
										:class="depot.checkedCount === depot.items.length ? 'bg-green-500' : 'bg-mabey-500'"
										:style="{ width: (depot.items.length > 0 ? (depot.checkedCount / depot.items.length) * 100 : 0) + '%' }"></div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Depot Items -->
				<div v-if="expandedDepots.has(depot.depotName)" class="divide-y divide-gray-100">

					<!-- Card layout: tablet and below (< xl) — two lines per item, no horizontal scroll -->
					<div class="xl:hidden divide-y divide-gray-100">
						<div
							v-for="item in depot.items"
							:key="item.id"
							class="px-4 py-3 transition-colors"
							:class="{
								'bg-green-50/30': item.stock_check_status === 'checked' || item.stock_check_status === 'manually_checked',
								'bg-amber-50/30': item.stock_check_status === 'stock_check_pending'
							}">
							<!-- Line 1: Item info + status + quantities -->
							<div class="flex items-start justify-between gap-3 mb-2">
								<div class="min-w-0 flex-1">
									<div class="flex items-center gap-2 flex-wrap">
										<span class="font-semibold text-gray-900 text-sm">{{ item.item_code }}</span>
										<span class="font-mono text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{{ item.contractNumber }}</span>
										<span
											class="text-xs font-medium"
											:class="getDaysClass(item.daysUntilDue)">
											{{ formatShortDate(item.required_by_date) }}
										</span>
									</div>
									<div class="text-xs text-gray-600 truncate mt-0.5" :title="item.description">{{ item.description }}</div>
								</div>
								<div class="flex items-center gap-2 flex-shrink-0">
									<div class="text-right text-xs text-gray-500 leading-tight">
										<div><span class="font-semibold text-gray-900">{{ item.allocation_quantity || item.quantity_requested }}</span> alloc</div>
										<div>{{ item.quantity_requested }} req</div>
									</div>
									<span v-if="item.stock_check_status === 'checked'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 whitespace-nowrap">
										<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Checked
									</span>
									<span v-else-if="item.stock_check_status === 'manually_checked'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 whitespace-nowrap">
										<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Manual
									</span>
									<span v-else-if="item.stock_check_status === 'stock_check_pending'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
										<font-awesome-icon icon="fas fa-clock" class="mr-1" /> Pending
									</span>
									<span v-else class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 whitespace-nowrap">
										<font-awesome-icon icon="fas fa-exclamation-circle" class="mr-1" /> Not Checked
									</span>
								</div>
							</div>
							<!-- Line 2: Action controls -->
							<div class="flex items-center justify-end gap-2">
								<template v-if="item.stock_check_status === 'checked' || item.stock_check_status === 'manually_checked'">
									<div class="text-right mr-1">
										<div class="text-base font-bold" :class="getConfirmedResultClass(item)" :title="item.stock_checked_at ? formatDateTime(item.stock_checked_at) : ''">
											{{ item.stock_check_result !== null && item.stock_check_result !== undefined ? item.stock_check_result : '—' }}
										</div>
										<div v-if="item.stock_checked_at" class="text-[10px] text-gray-400">{{ formatShortDate(item.stock_checked_at) }}</div>
									</div>
									<button
										class="px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-1 whitespace-nowrap"
										title="Re-check this item"
										@click="resetItemForRecheck(item)">
										<font-awesome-icon icon="fas fa-redo" class="text-xs" />
										<span>Re-check</span>
									</button>
								</template>
								<template v-else>
									<input
										v-model.number="item._inputQuantity"
										type="number"
										min="0"
										step="1"
										class="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 text-center font-semibold" />
									<button
										class="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
										title="Set to allocation quantity"
										@click="setMaxQuantity(item)">
										Max
									</button>
									<button
										:disabled="savingItems.has(item.id!)"
										class="px-3 py-1.5 text-xs font-medium rounded-md border transition-all flex items-center space-x-1.5 whitespace-nowrap"
										:class="savingItems.has(item.id!)
											? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
											: 'bg-green-600 text-white border-green-600 hover:bg-green-700'
										"
										@click="confirmItem(item)">
										<font-awesome-icon :icon="savingItems.has(item.id!) ? 'fas fa-spinner fa-spin' : 'fas fa-check'" />
										<span>{{ savingItems.has(item.id!) ? 'Saving...' : 'Confirm' }}</span>
									</button>
								</template>
							</div>
						</div>
					</div>

					<!-- Full table: desktop (xl+) -->
					<div class="hidden xl:block overflow-x-auto">
						<table class="w-full text-sm">
							<thead>
								<tr class="border-b border-gray-200 bg-gray-50">
									<th class="px-3 py-2 text-left text-xs font-semibold text-gray-600">Item Code</th>
									<th class="px-3 py-2 text-left text-xs font-semibold text-gray-600">Description</th>
									<th class="px-3 py-2 text-left text-xs font-semibold text-gray-600">Contract</th>
									<th class="px-3 py-2 text-left text-xs font-semibold text-gray-600">Destination</th>
									<th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">Req</th>
									<th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">Alloc</th>
									<th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">Due</th>
									<th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
									<th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">Last Checked</th>
									<th class="px-3 py-2 text-right text-xs font-semibold text-gray-600">Stock Check</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="item in depot.items"
									:key="item.id"
									class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
									:class="{
										'bg-green-50/30': item.stock_check_status === 'checked' || item.stock_check_status === 'manually_checked',
										'bg-amber-50/30': item.stock_check_status === 'stock_check_pending'
									}">
									<td class="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">{{ item.item_code }}</td>
									<td class="px-3 py-2 text-gray-700 max-w-[160px] truncate" :title="item.description">{{ item.description }}</td>
									<td class="px-3 py-2 text-gray-600 font-mono text-xs whitespace-nowrap">{{ item.contractNumber }}</td>
									<td class="px-3 py-2 text-gray-600">{{ item.destinationDepot }}</td>
									<td class="px-3 py-2 text-center font-semibold text-gray-900">{{ item.quantity_requested }}</td>
									<td class="px-3 py-2 text-center font-semibold text-gray-900">{{ item.allocation_quantity || item.quantity_requested }}</td>
									<td class="px-3 py-2 text-center font-medium whitespace-nowrap" :class="getDaysClass(item.daysUntilDue)">{{ formatShortDate(item.required_by_date) }}</td>
									<td class="px-3 py-2 text-center">
										<span v-if="item.stock_check_status === 'checked'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 whitespace-nowrap">
											<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Checked
										</span>
										<span v-else-if="item.stock_check_status === 'manually_checked'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 whitespace-nowrap">
											<font-awesome-icon icon="fas fa-check-circle" class="mr-1" /> Manual
										</span>
										<span v-else-if="item.stock_check_status === 'stock_check_pending'" class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
											<font-awesome-icon icon="fas fa-clock" class="mr-1" /> Pending
										</span>
										<span v-else class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 whitespace-nowrap">
											<font-awesome-icon icon="fas fa-exclamation-circle" class="mr-1" /> Not Checked
										</span>
									</td>
									<td class="px-3 py-2 text-center">
										<span v-if="item.stock_checked_at" class="text-xs text-gray-700">{{ formatDateTime(item.stock_checked_at) }}</span>
										<span v-else class="text-xs text-gray-400 italic">—</span>
									</td>
									<td class="px-3 py-2">
										<div v-if="item.stock_check_status === 'checked' || item.stock_check_status === 'manually_checked'" class="flex items-center justify-end gap-2">
											<div class="text-right">
												<div class="text-base font-bold" :class="getConfirmedResultClass(item)" :title="item.stock_checked_at ? formatDateTime(item.stock_checked_at) : ''">{{ item.stock_check_result !== null && item.stock_check_result !== undefined ? item.stock_check_result : '—' }}</div>
												<div v-if="item.stock_checked_at" class="text-[10px] text-gray-400">{{ formatShortDate(item.stock_checked_at) }}</div>
											</div>
											<button
												class="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-1 whitespace-nowrap"
												title="Re-check this item"
												@click="resetItemForRecheck(item)">
												<font-awesome-icon icon="fas fa-redo" class="text-xs" />
												<span>Re-check</span>
											</button>
										</div>
										<div v-else class="flex items-center justify-end gap-1">
											<input
												v-model.number="item._inputQuantity"
												type="number"
												min="0"
												step="1"
												class="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400 text-center font-semibold" />
											<button
												class="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
												title="Set to allocation quantity"
												@click="setMaxQuantity(item)">
												Max
											</button>
											<button
												:disabled="savingItems.has(item.id!)"
												class="px-2 py-1 text-xs font-medium rounded-md border transition-all flex items-center space-x-1 whitespace-nowrap"
												:class="savingItems.has(item.id!)
													? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
													: 'bg-green-600 text-white border-green-600 hover:bg-green-700'
												"
												@click="confirmItem(item)">
												<font-awesome-icon :icon="savingItems.has(item.id!) ? 'fas fa-spinner fa-spin' : 'fas fa-check'" />
												<span>{{ savingItems.has(item.id!) ? 'Saving...' : 'Confirm' }}</span>
											</button>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>

		<!-- Reset Confirmation Modal -->
		<teleport to="body">
			<div v-if="showResetModal && itemToReset" class="fixed inset-0 z-50 flex items-center justify-center">
				<div class="absolute inset-0 bg-black/50" @click="showResetModal = false"></div>
				<div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
					<div class="px-6 py-4 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">Reset Stock Check</h2>
					</div>
					<div class="p-6">
						<div class="flex items-start space-x-3">
							<font-awesome-icon icon="fas fa-exclamation-triangle" class="text-amber-500 text-xl mt-0.5" />
							<div>
								<p class="text-sm text-gray-700 mb-2">This will re-submit this item for checking, are you sure?</p>
								<p class="text-xs text-gray-500">Item: <span class="font-medium text-gray-700">{{ itemToReset.item_code }}</span></p>
							</div>
						</div>
					</div>
					<div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
						<button
							class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
							@click="showResetModal = false">
							Cancel
						</button>
						<button
							class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-1.5"
							@click="confirmReset">
							<font-awesome-icon icon="fas fa-rotate-left" />
							<span>Continue</span>
						</button>
					</div>
				</div>
			</div>
		</teleport>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import type { StockShortageResponse, StockShortageItem } from '@/data/schema/stock-shortage'
	import StockService from '@/services/stock'
	import useToaster from '@/stores/toaster'
	import useAuthStore from '@/stores/auth'
	import AppButton from '@/components/base/AppButton.vue'
	import AppBadge from '@/components/base/AppBadge.vue'
	import AppLoader from '@/components/base/AppLoader.vue'

	interface StockCheckItem extends StockShortageItem {
		requestId: string
		contractNumber: string
		destinationDepot: string
		sourceDepot: string
		daysUntilDue: number
		_inputQuantity?: number | null
	}

	interface DepotGroup {
		depotName: string
		items: StockCheckItem[]
		pendingCount: number
		checkedCount: number
	}

	const stockService = new StockService()
	const toaster = useToaster()
	const auth = useAuthStore()

	const loading = ref(false)
	const requests = ref<StockShortageResponse[]>([])
	const expandedDepots = ref<Set<string>>(new Set())
	const savingItems = ref<Set<string>>(new Set())
	const searchFilter = ref('')
	const statusFilter = ref<'all' | 'pending' | 'checked'>('all')
	const showResetModal = ref(false)
	const itemToReset = ref<StockCheckItem | null>(null)

	const statusFilterOptions = [
		{ label: 'All', value: 'all' as const },
		{ label: 'Pending', value: 'pending' as const },
		{ label: 'Checked', value: 'checked' as const }
	]

	// Extract all items that are flagged-for-transfer and have a source depot, grouped by source depot
	// We augment the original reactive item objects in-place so that mutations in confirmItem/resetItemForRecheck
	// are detected by Vue's reactivity system and trigger recomputation of depotGroups.
	const allStockCheckItems = computed((): StockCheckItem[] => {
		const result: StockCheckItem[] = []
		const now = Date.now()

		for (const req of requests.value) {
			for (const item of req.items) {
				if (item.fulfillment_status !== 'flagged-for-transfer') continue
				if (!item.allocation_from_depot) continue

				const daysUntilDue = Math.ceil((new Date(item.required_by_date).getTime() - now) / 86400000)

				// Augment original item in-place to preserve reactivity
				const augmented = item as StockCheckItem
				augmented.requestId = req.id
				augmented.contractNumber = req.contract_number
				augmented.destinationDepot = req.depot
				augmented.sourceDepot = item.allocation_from_depot
				augmented.daysUntilDue = daysUntilDue
				if (augmented._inputQuantity === undefined) {
					augmented._inputQuantity = null
				}

				result.push(augmented)
			}
		}

		return result
	})

	const depotGroups = computed((): DepotGroup[] => {
		const depotMap = new Map<string, StockCheckItem[]>()

		for (const item of allStockCheckItems.value) {
			if (!depotMap.has(item.sourceDepot)) {
				depotMap.set(item.sourceDepot, [])
			}
			depotMap.get(item.sourceDepot)!.push(item)
		}

		const groups: DepotGroup[] = []
		for (const [depotName, items] of depotMap.entries()) {
			const pendingCount = items.filter(i => i.stock_check_status === 'stock_check_pending' || !i.stock_check_status || i.stock_check_status === 'not_checked').length
			const checkedCount = items.filter(i => i.stock_check_status === 'checked' || i.stock_check_status === 'manually_checked').length

			groups.push({
				depotName,
				items: items.sort((a, b) => a.item_code.localeCompare(b.item_code)),
				pendingCount,
				checkedCount
			})
		}

		return groups.sort((a, b) => a.depotName.localeCompare(b.depotName))
	})

	const filteredDepotGroups = computed((): DepotGroup[] => {
		return depotGroups.value
			.map(depot => {
				let filteredItems = depot.items

				// Apply search filter
				if (searchFilter.value) {
					const search = searchFilter.value.toLowerCase()
					filteredItems = filteredItems.filter(item =>
						item.item_code.toLowerCase().includes(search) ||
						item.description.toLowerCase().includes(search) ||
						item.contractNumber.toLowerCase().includes(search) ||
						item.destinationDepot.toLowerCase().includes(search) ||
						item.sourceDepot.toLowerCase().includes(search)
					)
				}

				// Apply status filter
				if (statusFilter.value === 'pending') {
					filteredItems = filteredItems.filter(i =>
						i.stock_check_status === 'stock_check_pending' || !i.stock_check_status || i.stock_check_status === 'not_checked'
					)
				} else if (statusFilter.value === 'checked') {
					filteredItems = filteredItems.filter(i =>
						i.stock_check_status === 'checked' || i.stock_check_status === 'manually_checked'
					)
				}

				if (filteredItems.length === 0) return null

				const pendingCount = filteredItems.filter(i => i.stock_check_status === 'stock_check_pending' || !i.stock_check_status || i.stock_check_status === 'not_checked').length
				const checkedCount = filteredItems.filter(i => i.stock_check_status === 'checked' || i.stock_check_status === 'manually_checked').length

				return {
					depotName: depot.depotName,
					items: filteredItems,
					pendingCount,
					checkedCount
				}
			})
			.filter(Boolean) as DepotGroup[]
	})

	const pendingItemCount = computed(() => {
		return allStockCheckItems.value.filter(i =>
			i.stock_check_status === 'stock_check_pending' || !i.stock_check_status || i.stock_check_status === 'not_checked'
		).length
	})

	const uniqueDepotCount = computed(() => {
		return depotGroups.value.length
	})

	const toggleDepot = (depotName: string) => {
		const newSet = new Set(expandedDepots.value)
		if (newSet.has(depotName)) {
			newSet.delete(depotName)
		} else {
			newSet.add(depotName)
		}
		expandedDepots.value = newSet
	}

	const confirmItem = async (item: StockCheckItem) => {
		if (!item.id) return
		
		// Get quantity from input, default to 0 if empty
		const qty = item._inputQuantity ? parseFloat(String(item._inputQuantity)) : 0
		if (isNaN(qty)) {
			toaster.error({ text: 'Please enter a valid number' })
			return
		}

		const newSaving = new Set(savingItems.value)
		newSaving.add(item.id)
		savingItems.value = newSaving

		try {
			await stockService.confirmStockCheck(item.id, qty)

			// Update local state reactively
			item.stock_check_status = 'checked'
			item.stock_checked_at = new Date().toISOString()
			item.stock_check_result = qty

			toaster.success({ text: `Stock confirmed for ${item.item_code}: ${qty} available` })
		} catch (error) {
			console.error('Error confirming stock check:', error)
			toaster.error({ text: `Failed to confirm stock for ${item.item_code}` })
		} finally {
			const newSaving2 = new Set(savingItems.value)
			newSaving2.delete(item.id)
			savingItems.value = newSaving2
		}
	}

	const resetItemForRecheck = (item: StockCheckItem) => {
		// Prompt the user if item is already checked
		if (item.stock_check_status === 'checked' || item.stock_check_status === 'manually_checked') {
			itemToReset.value = item
			showResetModal.value = true
			return
		}

		performReset(item)
	}

	const confirmReset = () => {
		if (itemToReset.value) {
			performReset(itemToReset.value)
		}
		showResetModal.value = false
		itemToReset.value = null
	}

	const performReset = (item: StockCheckItem) => {
		// Reset item state immediately for reactivity
		item.stock_check_status = 'stock_check_pending'
		item._inputQuantity = null
		item.stock_check_result = null
		item.stock_checked_at = null
	}

	const setMaxQuantity = (item: StockCheckItem) => {
		const maxQty = item.allocation_quantity || item.quantity_requested
		item._inputQuantity = maxQty
	}

	const getConfirmedResultClass = (item: StockCheckItem): string => {
		if (typeof item.stock_check_result === 'number') {
			return item.stock_check_result >= (item.allocation_quantity || item.quantity_requested) ? 'text-green-700' : 'text-red-700 font-bold'
		}
		return 'text-gray-400'
	}

	const getDaysClass = (days: number): string => {
		if (days < 0) return 'text-red-600'
		if (days <= 2) return 'text-orange-500'
		if (days <= 7) return 'text-yellow-600'
		return 'text-green-600'
	}

	const formatShortDate = (dateString: string): string => {
		if (!dateString) return '—'
		const date = new Date(dateString)
		return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
	}

	const formatDateTime = (dateString: string): string => {
		if (!dateString) return '—'
		const date = new Date(dateString)
		return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
	}

	const loadRequests = async () => {
		loading.value = true
		try {
			const response = await stockService.getStockCheckPendingRequests(auth.user?.assigned_depots)

			if (Array.isArray(response)) {
				requests.value = response
			} else if (response && Array.isArray((response as any).data)) {
				requests.value = (response as any).data
			} else if (response && (response as any).data && Array.isArray((response as any).data.data)) {
				requests.value = (response as any).data.data
			} else {
				console.warn('Unexpected API response format:', response)
				requests.value = []
			}
		} catch (error) {
			console.error('Error loading stock check requests:', error)
			toaster.error({ text: 'Failed to load stock check requests' })
			requests.value = []
		} finally {
			loading.value = false
		}
	}

	const refreshData = () => {
		loadRequests()
	}

	onMounted(() => {
		loadRequests()
	})
</script>
